import React from "react";

type Props = {
  className?: string;
  ariaLabel?: string;
};

function requestIdle(cb: () => void) {
  if (typeof window === "undefined") return;
  const ric = (window as unknown as { requestIdleCallback?: (fn: () => void, opts?: { timeout?: number }) => void })
    .requestIdleCallback;
  if (typeof ric === "function") {
    ric(cb, { timeout: 1500 });
    return;
  }
  window.setTimeout(cb, 250);
}

export function LazyAutoplayVideo({ className, ariaLabel }: Props) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [src, setSrc] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    // Load as soon as the /reweaving page mounts (still in the background via idle).
    requestIdle(async () => {
      try {
        const mod = await import("../assets/video/demo.mp4");
        if (!cancelled) setSrc(mod.default ?? (mod as unknown as string));
      } catch {
        // If the asset fails to resolve, keep placeholder.
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!src) return;
    const v = videoRef.current;
    if (!v) return;
    // Autoplay can be blocked; muted+playsInline usually works, but be safe.
    v.play?.().catch(() => {});
  }, [src]);

  return (
    <div aria-label={ariaLabel}>
      {src ? (
        <video
          ref={videoRef}
          className={className}
          src={src}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        />
      ) : (
        <div className="tech-media tech-media-placeholder" aria-busy="true" />
      )}
    </div>
  );
}


